import React, { useState } from 'react'
import { Button, Form, message, Select, Spin, Table } from 'antd'
import ProjectSelector from '../../ProjectSelector'
import { APP_STATUS_AGREE, APP_STATUS_DELIVERY, APP_STATUS_DONE, APP_STATUS_PAY, statuses } from '../constants'
import { getReportByProject } from '../../../services/application'
import { updateApplicationItem } from '../../../services/application-items'
import EstimateAutoSuggestion from '../../EstimateAutoSuggestion'
import EstimateSuggestion from '../../EstimateSuggestion'
import useTableFilter from '../../hooks/useTableFilter'
import { formatSum } from '../../../utils/number-utils'
import get from 'lodash/get'
import { getOffBudget } from '../../application/app-view-utils'

const defaultStatuses = [APP_STATUS_AGREE, APP_STATUS_PAY, APP_STATUS_DELIVERY, APP_STATUS_DONE]

const numberCellStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  overflowWrap: 'normal',
}

const EstimateCategory = () => {
  const [form] = Form.useForm()
  const [data, setData] = useState([])
  const [project, setProject] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isManualModalOpen, setIsManualModalOpen] = useState(false)
  const [selectedRecordId, setSelectedRecordId] = useState(null)
  const [selectedRecord, setSelectedRecord] = useState(null)

  const [filter, searchText, searchedColumn, getColumnSearchProps] = useTableFilter()

  const columns = [
    {
      title: 'Заявка',
      dataIndex: 'applicationId',
      key: 'applicationId',
      render: (applicationId) => (
        <a href={`/applications?state=all&id=${applicationId}`} target="_blank" rel="noreferrer">
          {applicationId}
        </a>
      ),
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      onFilter: (value, record) => get(record, 'name').toString().toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Наименование заявка',
      dataIndex: 'applicationItemName',
      key: 'applicationItemName',
      ...getColumnSearchProps('applicationItemName'),
      onFilter: (value, record) =>
        get(record, 'applicationItemName').toString().toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Кол-во факт',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <div style={numberCellStyle}>{formatSum(amount)}</div>,
    },
    {
      title: 'Ед.изм.',
      dataIndex: 'unit',
      key: 'unit',
    },
    {
      title: 'Наименование сметы',
      dataIndex: 'estimateName',
      key: 'estimateName',
    },
    {
      title: 'Категория сметы',
      dataIndex: 'estimateCategory',
      key: 'estimateCategory',
    },
    {
      title: 'Вне сметы',
      dataIndex: 'off_budget',
      key: 'off_budget',
      render: (off_budget) => {
        return off_budget ? getOffBudget(off_budget) : ''
      },
    },
    {
      title: 'Действия',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => {
        return (
          <div>
            <Button
              type="link"
              onClick={() => {
                setSelectedRecordId(record?.applicationItemId)
                setSelectedRecord(record)
                setIsModalOpen(true)
              }}
            >
              Выбрать из автоподбора
            </Button>
            <Button
              type="link"
              onClick={() => {
                setIsManualModalOpen(true)
                setSelectedRecordId(record?.applicationItemId)
                setSelectedRecord(record)
              }}
            >
              Выбрать из сметы
            </Button>
          </div>
        )
      },
    },
  ]

  const onFormSubmit = (values) => {
    setIsLoading(true)

    const { project, statuses } = values
    setProject(project)

    getReportByProject(project, { statuses })
      .then((response) => {
        const results = response?.data?.data?.attributes?.results

        if (results) {
          const items = results.reduce((acc, item) => {
            const findImplementation = (appItemId) =>
              item?.implementation.implementation_items.filter((value) => value?.application_item?.id === appItemId)
            item?.application_items?.forEach((application_item) => {
              const implementations = findImplementation(application_item.id)
              if (implementations?.length > 0) {
                implementations.forEach((implementation) => {
                  acc.push({
                    id: implementation?.id,
                    applicationId: item?.id,
                    applicationItemId: application_item?.id,
                    name: implementation?.name,
                    amount:
                      implementation?.actualAmount !== null && implementation?.actualAmount >= 0
                        ? implementation?.actualAmount
                        : implementation.amount,
                    unit: implementation?.unit,
                    applicationItemName: application_item?.name,
                    estimateName: application_item?.estimate_field?.name,
                    estimateCategory: application_item?.category || application_item?.estimate_field?.category,
                    off_budget: application_item?.off_budget,
                  })
                })
              }
            })

            return acc
          }, [])
          setData(items)
        }
      })
      .finally(() => setIsLoading(false))
  }

  const onRecordUpdate = (record) => {
    updateApplicationItem(selectedRecordId, {
      data: {
        estimate_field: record.id,
      },
    })
      .then(() => {
        const newData = data.map((item) => {
          if (item?.applicationItemId === selectedRecordId) {
            return {
              ...item,
              estimateName: record.name,
              estimateCategory: record.category,
            }
          }

          return item
        })
        setData(newData)
        handleCancel()
        message.success('Заявка успешно обновлена')
      })
      .catch((error) => message.error('Ошибка при обновлении заявки'))
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedRecordId(null)
    setSelectedRecord(null)
  }

  return (
    <Spin spinning={isLoading}>
      <div style={{ paddingTop: 10 }}>
        <Form
          form={form}
          initialValues={{ statuses: defaultStatuses }}
          layout="vertical"
          style={{ display: 'flex' }}
          onFinish={onFormSubmit}
        >
          <Form.Item
            style={{ width: 300, maxWidth: 300 }}
            rules={[{ required: true, message: 'Выберите проект' }]}
            label="Проект"
            name="project"
          >
            <ProjectSelector />
          </Form.Item>
          <Form.Item style={{ marginLeft: 15 }} label="Статусы заявки" name="statuses">
            <Select mode="multiple" allowClear options={statuses} dropdownMatchSelectWidth={false} />
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button onClick={() => form.submit()}>Сформировать отчет</Button>
          </Form.Item>
        </Form>
        <Table
          id="estimateCategoryTable"
          columns={columns}
          dataSource={data}
          bordered
          size="small"
          pagination={false}
        />
      </div>
      <EstimateAutoSuggestion
        isModalOpen={isModalOpen}
        handleCancel={handleCancel}
        selectedRecord={selectedRecord}
        project={project}
        onUpdate={onRecordUpdate}
      />
      <EstimateSuggestion
        isModalOpen={isManualModalOpen}
        handleCancel={() => setIsManualModalOpen(false)}
        project={project}
        onUpdate={onRecordUpdate}
      />
    </Spin>
  )
}

export default EstimateCategory
